import {
  buildLibraryDisplayItems,
  getLibraryItems,
  type LibraryResetPlayerPayload,
  queryKeys,
  resetPlayerPayloadFromLibraryItem,
} from "@healthos/client-data";
import { Play } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { client } from "../api/healthos";
import { ResetMediaLauncher } from "../components/media-player/reset-media-launcher";
import { ContactCentreResetPicker } from "./contact-centre-reset-picker";
import { ContactCentreWaves } from "./contact-centre-waves";

const TITLE = "HealthOS for Contact Centres";
const SUBHEADING = "A short Reset between conversations.";
const LIBRARY_QUERY = { kind: "Reset" as const, page: 1, limit: 24 };

export function ContactCentreHome() {
  const catalogQuery = useQuery({
    queryKey: queryKeys.libraryPage(LIBRARY_QUERY),
    queryFn: () => getLibraryItems(client(), LIBRARY_QUERY),
  });
  const playable = useMemo(() => {
    const items = buildLibraryDisplayItems(catalogQuery.data?.items ?? []);
    return items.filter((item) => resetPlayerPayloadFromLibraryItem(item) !== null);
  }, [catalogQuery.data]);

  const [browsing, setBrowsing] = useState(false);
  const [activeReset, setActiveReset] = useState<LibraryResetPlayerPayload | null>(null);

  const canOpen = playable.length > 0;
  const unavailable = catalogQuery.isError || (catalogQuery.isSuccess && !canOpen);

  return (
    <section className="relative isolate min-h-dvh overflow-hidden bg-cream-warm">
      <ContactCentreWaves />

      <div
        aria-hidden={browsing}
        className={`relative z-10 grid min-h-dvh place-items-center px-6 py-20 transition-opacity duration-500 ease-out ${
          browsing ? "pointer-events-none opacity-0" : "opacity-100"
        }`}
      >
        <div className="flex w-full max-w-xl flex-col items-center text-center">
          <h1
            aria-label={TITLE}
            className="m-0 font-title text-4xl font-medium leading-[1.08] tracking-[-0.02em] text-navy sm:text-5xl"
          >
            <span className="block">HealthOS for</span>
            <span className="block">Contact Centres</span>
          </h1>
          <p className="mt-4 max-w-[34ch] font-sans text-base leading-relaxed text-navy/72 sm:text-lg">
            {SUBHEADING}
          </p>

          <div className="mt-10 flex min-h-[7.5rem] flex-col items-center justify-start">
            {catalogQuery.isPending ? <PlayButtonSkeleton /> : null}
            {canOpen ? <PlayResetButton onClick={() => setBrowsing(true)} /> : null}
            {unavailable ? <UnavailableCopy /> : null}
          </div>
        </div>
      </div>

      {browsing && canOpen ? (
        <ContactCentreResetPicker
          items={playable}
          paused={Boolean(activeReset)}
          onSelect={setActiveReset}
          onClose={() => setBrowsing(false)}
        />
      ) : null}

      {activeReset ? (
        <ResetMediaLauncher
          isOpen
          reset={activeReset}
          onClose={() => setActiveReset(null)}
          onComplete={() => setActiveReset(null)}
        />
      ) : null}
    </section>
  );
}

function PlayResetButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label="Play Reset"
      onClick={onClick}
      className="group flex cursor-pointer flex-col items-center gap-3 focus-visible:outline-none"
    >
      <span className="grid h-20 w-20 place-items-center rounded-full bg-navy text-paper shadow-[0_18px_40px_-18px_color-mix(in_srgb,var(--color-navy)_70%,transparent)] transition-transform duration-200 group-hover:scale-[1.04] group-active:scale-[0.96] group-focus-visible:ring-2 group-focus-visible:ring-teal-500 group-focus-visible:ring-offset-4 group-focus-visible:ring-offset-cream-warm">
        <Play size={28} weight="fill" className="block" aria-hidden="true" />
      </span>
      <span className="font-mono text-2xs font-semibold uppercase tracking-[0.18em] text-navy/70">
        Play
      </span>
    </button>
  );
}

function PlayButtonSkeleton() {
  return (
    <div className="flex flex-col items-center gap-3" aria-hidden="true">
      <span className="h-20 w-20 animate-pulse rounded-full bg-navy/15" />
      <span className="h-3 w-10 animate-pulse rounded-sm bg-navy/10" />
    </div>
  );
}

function UnavailableCopy() {
  return (
    <div className="max-w-sm text-center">
      <p className="m-0 font-sans text-base font-medium text-navy">
        No Resets are available right now.
      </p>
      <p className="m-0 mt-2 font-sans text-sm leading-relaxed text-gray-600">
        Try again in a moment. If they stay missing, ask a HealthOS admin.
      </p>
    </div>
  );
}
